/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Checkbox, CircularProgress, TextField,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';

import Page from '../../../components/Page';
import subscriptionServices from '../../../services/subscriptionServices';
import adminServices from '../../../services/adminServices';
import ErrorFocus from '../../../components/ErrorFocus/ErrorFocus';
import SnackBar from '../../../components/snackbar/snackbar';

import {
  SectionCard, FieldLabel, FeatureBullets,
  BackLink, PageHeading, SearchBox, CoverImageUpload,
} from './components/shared/SharedComponents';

const BillingToggle = ({ value, onChange }) => (
  <div className="flex border border-gray-200 rounded-[7px] overflow-hidden">
    {['Yearly', 'Monthly'].map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className="flex-1 text-center px-4 py-[9px] text-[13px] font-medium cursor-pointer select-none transition-all"
        style={{
          background: value === opt ? '#eff6ff' : '#fff',
          color: value === opt ? '#1d4ed8' : '#374151',
          border: value === opt ? '1.5px solid #2563eb' : '1px solid transparent',
          borderRadius: value === opt ? '6px' : 0,
        }}
      >
        {opt}
      </button>
    ))}
  </div>
);

// ── Content-section inner tab bar 
const CONTENT_TABS = [
  { key: 'ebooks', label: 'E-Books' },
  { key: 'courses', label: 'Courses' },
  { key: 'assessments', label: 'Assessments' },
];

const ContentTabBar = ({ active, onChange }) => (
  <div className="flex border-b border-gray-200 mb-3">
    {CONTENT_TABS.map((t) => (
      <button
        key={t.key}
        type="button"
        onClick={() => onChange(t.key)}
        className={`px-4 py-2 text-[13px] font-medium cursor-pointer bg-transparent border-0 border-b-2 -mb-px transition-colors duration-150 ${
          active === t.key
            ? 'text-blue-600 border-blue-600'
            : 'text-gray-500 border-transparent hover:text-gray-800'
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const ItemList = ({ loading, items, selectedIds, onToggle, emptyText }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <CircularProgress size={20} sx={{ color: '#2563eb' }} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <p className="text-[13px] text-gray-400">{emptyText}</p>
      </div>
    );
  }
  return items.map((item, idx) => {
    const checked = selectedIds.includes(item.id);
      return (
        <div
            key={item.id}
            role="button"
            tabIndex={0}
            className="flex items-center px-[14px] py-[10px] hover:bg-gray-50 cursor-pointer"
            style={{ borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none' }}
            onClick={() => onToggle(item.id, checked)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(item.id, checked); }}
        >
        <Checkbox
          size="small"
          checked={checked}
          onChange={() => onToggle(item.id, checked)}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: '4px', mr: '10px', color: '#d1d5db', '&.Mui-checked': { color: '#2563eb' } }}
        />
        <div>
            <p className="text-[13px] font-medium text-gray-900">{item.title || item.name}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-[0.04em]">
                {item.author || item.type || item.courseType || '—'}
                {item.board && <span className="ml-[6px]">· {item.board}</span>}
                {item.assessment_sub_type_description && (
                    <span className="ml-[6px]">· {item.assessment_sub_type_description}</span>
                )}
                {item.isIndividuallySold && (
                    <span className="text-amber-500 ml-[6px]">· Also sold individually</span>
                )}
            </p>
        </div>
      </div>
    );
  });
};

const CreateEbookPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTab = location.state?.fromTab ?? 4;

  const [ebooks, setEbooks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [lookups, setLookups] = useState({ subscription_ids: [], currency_type: [] });

  const [contentLoading, setContentLoading] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [submittingPublish, setSubmittingPublish] = useState(false);

  const [ebookSearch, setEbookSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [assessmentSearch, setAssessmentSearch] = useState('');

  const [contentTab, setContentTab] = useState('ebooks');
  const [pricingMode, setPricingMode] = useState('');

  const [coverImage, setCoverImage] = useState(null);
  const [coverImgPreview, setCoverImgPreview] = useState(null);
  const [coverImageError, setCoverImageError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, title: '' });

  const publishModeRef = React.useRef('draft');

  useEffect(() => {
    fetchAllContent();
    fetchLookups();
  }, []);

  const fetchLookups = async () => {
    try {
      const res = await adminServices.getLookUpData('subscription_ids,currency_type');
      if (res.ok) setLookups(res.data || {});
    } catch (e) { console.error('[CreateEbookPlan lookups]', e); }
  };

  const fetchAllContent = async () => {
      setContentLoading(true);
      try {
          const [ebookRes, courseRes, assessmentRes] = await Promise.all([
              adminServices.getEbooks('', 1, 500),
              adminServices.getLookUpData(['course']),
              adminServices.getGenericAssessmentDropDown({ page: 1, pageSize: 500, audience: 'B2C' }),
          ]);

          if (ebookRes.ok) {
              let ebookRows = [];
              if (Array.isArray(ebookRes.data)) ebookRows = ebookRes.data;
              else if (Array.isArray(ebookRes.data?.rows)) ebookRows = ebookRes.data.rows;
              setEbooks(ebookRows);
          }
          if (courseRes.ok) {
              setCourses(
                  (courseRes.data.course || []).filter((c) => {
                      const code = (c.audienceCode || '').toUpperCase();
                      return Number(c.status) === 1 && !c.isFree && (code === 'B2C' || code === 'BOTH');
                  })
              );
          }
          if (assessmentRes.ok) {
              let rows = [];
              if (Array.isArray(assessmentRes.data?.rows)) rows = assessmentRes.data.rows;
              else if (Array.isArray(assessmentRes.data)) rows = assessmentRes.data;
              setAssessments(rows.filter((a) => a.audience === 'B2C' || a.audience === 'BOTH'));
          }
      } catch (e) { console.error('[CreateEbookPlan fetchAllContent]', e); }
      setContentLoading(false);
  };

  const filteredEbooks = ebooks.filter((e) =>
    (e.title || e.name || '').toLowerCase().includes(ebookSearch.toLowerCase())
    );
    const filteredCourses = courses.filter((c) =>
        (c.title || '').toLowerCase().includes(courseSearch.toLowerCase())
    );
    const filteredAssessments = assessments.filter((a) =>
        (a.title || '').toLowerCase().includes(assessmentSearch.toLowerCase())
    );

  const imageWidthAndHeight = (provideFile) => {
    if (!provideFile) return Promise.resolve({ width: null, height: null });
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.src = reader.result;
      };
      reader.readAsDataURL(provideFile);
    });
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    setCoverImage(null);
    setCoverImgPreview(null);
    setCoverImageError('');
    if (!file) return;

    const acceptedTypes = ['image/jpeg', 'image/png', 'image/bmp'];
    if (!acceptedTypes.includes(file.type)) {
      setCoverImageError('Only JPG, PNG, BMP allowed');
      return;
    }
    if (file.size > 104857600) {
      setCoverImageError('Max file size is 100 MB');
      return;
    }
    imageWidthAndHeight(file).then((res) => {
      if (res.width >= 1200 && res.height >= 400) {
        setCoverImage(file);
        setCoverImgPreview(URL.createObjectURL(file));
      } else {
        setCoverImageError('Image must be at least 1200 × 400 px');
      }
    });
  };

  const initialValues = {
    planName: '',
    displayName: '',
    description: '',
    tagline: '',
    price: '',
    priceUSD: '',
    billingCycle: 'Yearly',
    featureBullets: ['', '', ''],
    footnote: '',
    stripePriceId: '',
    selectedEbooks: [],
    selectedCourses: [],
    selectedAssessments: [],
    pricingMode: 'Paid',
  };

  const validationSchema = (img) =>
    Yup.object()
      .shape({
        planName: Yup.string().trim().min(3, 'Min 3 characters').required('Required'),
        price: Yup.number().min(0, 'Must be ≥ 0').when('pricingMode', {
          is: 'Paid',
          then: (s) => s.required('Required'),
          otherwise: (s) => s.notRequired(),
        }),
        priceUSD: Yup.number().min(0, 'Must be ≥ 0').when('pricingMode', {
          is: 'Paid',
          then: (s) => s.required('Required'),
          otherwise: (s) => s.notRequired(),
        }),
        selectedEbooks: Yup.array().when(['selectedCourses', 'selectedAssessments'], {
          is: (courses, assessments) => !courses?.length && !assessments?.length,
          then: (s) => s.min(1, 'Select at least one e-book, course, or assessment'),
          otherwise: (s) => s.notRequired(),
        }),
      })

  const handleSubmit = async (values, publishMode = 'draft') => {
    if (publishMode === 'draft') setSubmittingDraft(true);
    else setSubmittingPublish(true);

    try {
      const fd = new FormData();
      fd.append('name', values.planName);
      fd.append('shortDescription', JSON.stringify(values.displayName ? [values.displayName] : []));
      fd.append('description', values.description || '');
      fd.append('tagline', values.tagline || '');
      fd.append('planType', 'ebook');
      fd.append('plan_category', 'EBOOK');
      fd.append('scope', 'platform');
      fd.append('subscriptionPlan', values.billingCycle === 'Yearly' ? 'Online Yearly Ebook' : 'Online Monthly - Ebook');
      fd.append('subscriptionType', 'B2C');
      fd.append('isOnline', publishMode === 'publish' ? 'true' : 'false');
      // "Publish Plan" = make the plan live so its eBooks reach end users, same
      // as the assessment plan flow (CreateAssessmentPlan sends is_active too).
      // A free plan needs no Stripe, so this alone makes it visible; paid plans
      // still need the "Make Live" step for Stripe.
      fd.append('is_active', publishMode === 'publish' ? 'true' : 'false');
      const isFree = values.pricingMode === 'Free';
      fd.append('isPaid', isFree ? 'false' : 'true');
      fd.append('frequency', values.billingCycle === 'Yearly' ? '1' : '2');

      // Content IDs
      fd.append('ebookIds', JSON.stringify(values.selectedEbooks));
      fd.append('courseIds', JSON.stringify(values.selectedCourses));
      fd.append('assessmentIds', JSON.stringify(values.selectedAssessments));

      fd.append('feature_bullets', JSON.stringify(values.featureBullets.filter(Boolean)));
      fd.append('footnote', values.footnote || '');

      if (values.stripePriceId) fd.append('stripePriceId', values.stripePriceId);
      if (coverImage) fd.append('coverImage', coverImage);

      const currencies = (lookups.currency_type || []).length > 0
        ? lookups.currency_type
        : [{ code: 'INR' }, { code: 'USD' }];

      const getPrice = (code) => code === 'INR' ? Number(values.price) || 0 : Number(values.priceUSD) || 0;
      fd.append('prices', JSON.stringify(
        currencies.map((c) => ({
          currencyType: c.code,
          actualCost: isFree ? 0 : getPrice(c.code),        }))
      ));

      const subIds = lookups.subscription_ids || [];
      const subName = values.billingCycle === 'Yearly' ? 'Online Yearly Ebook' : 'Online Monthly - Ebook';
      const subscriptionId = subIds.find((s) =>
        s.name.replace(/[\s-]+/g, ' ').toLowerCase().includes(subName.replace(/[\s-]+/g, ' ').toLowerCase()) ||
        subName.replace(/[\s-]+/g, ' ').toLowerCase().includes(s.name.replace(/[\s-]+/g, ' ').toLowerCase())
      )?.id;

      if (!subscriptionId) {
        setSnackbar({ open: true, title: `Subscription record "${subName}" not found. Contact admin.` });
        if (publishMode === 'draft') setSubmittingDraft(false); else setSubmittingPublish(false);
        return;
      }

      const res = await subscriptionServices.postSubscriptionPlan(subscriptionId, fd, 'ebooks');
      if (res.ok) {
        navigate(`/app/subscription?tab=${fromTab}`, {
          state: { message: publishMode === 'publish' ? 'Plan published!' : 'Draft saved' },
        });
      } else {
        setSnackbar({ open: true, title: res.data?.message || 'Something went wrong' });
      }
    } catch (e) {
      console.error('[CreateEbookPlan]', e);
      setSnackbar({ open: true, title: 'Something went wrong' });
    }

    if (publishMode === 'draft') setSubmittingDraft(false);
    else setSubmittingPublish(false);
  };

  return (
    <Page title="">
      <div className="max-w-[720px] mx-auto px-4 md:px-8 py-6">
        <BackLink
          label="Back to Plans"
          onClick={() => navigate(`/app/subscription?tab=${fromTab}`)}
        />
        <PageHeading
          title="Create E-Book Plan"
          subtitle="Define a B2C e-book bundle plan with annual subscription pricing."
        />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(coverImage)}
          onSubmit={(v) => handleSubmit(v, publishModeRef.current)}
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitCount }) => {

            const toggleId = (field, id, checked) => {
              setFieldValue(
                field,
                checked
                  ? values[field].filter((i) => i !== id)
                  : [...values[field], id]
              );
            };

            const tabConfig = {
              ebooks: {
                search: ebookSearch,
                setSearch: setEbookSearch,
                placeholder: 'Search e-books...',
                items: filteredEbooks,
                field: 'selectedEbooks',
                emptyText: 'No e-books available',
              },
              courses: {
                search: courseSearch,
                setSearch: setCourseSearch,
                placeholder: 'Search courses...',
                items: filteredCourses,
                field: 'selectedCourses',
                emptyText: 'No courses available',
              },
              assessments: {
                search: assessmentSearch,
                setSearch: setAssessmentSearch,
                placeholder: 'Search assessments...',
                items: filteredAssessments,
                field: 'selectedAssessments',
                emptyText: 'No assessments available',
              },
            };

            const activeConfig = tabConfig[contentTab];

            return (
              <Form>
                <ErrorFocus />

                {/* 1 — Plan Identity */}
                <SectionCard
                  step={1}
                  title="Plan Identity"
                  subtitle="Internal name, customer-facing name, and marketing copy."
                >
                  <div className="flex flex-col gap-[18px]">
                    <div>
                      <FieldLabel required>Plan name (internal)</FieldLabel>
                      <TextField
                        fullWidth
                        name="planName"
                        placeholder="e.g. Healthcare E-Book Bundle"
                        value={values.planName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.planName && errors.planName)}
                        helperText={touched.planName && errors.planName}
                        size="small"
                      />
                    </div>
                    <div>
                      <FieldLabel>Display name (customer-facing)</FieldLabel>
                      <TextField
                        fullWidth
                        name="displayName"
                        placeholder="e.g. Healthcare E-Book Bundle"
                        value={values.displayName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        Shown on pricing page. Defaults to plan name if blank.
                      </p>
                    </div>
                    <div>
                      <FieldLabel>Description</FieldLabel>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        name="description"
                        placeholder="Briefly describe what this plan includes..."
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                      />
                    </div>
                    <div>
                      <FieldLabel>Tagline</FieldLabel>
                      <TextField
                        fullWidth
                        name="tagline"
                        placeholder="e.g. Master compliance in one go"
                        value={values.tagline}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size="small"
                      />
                    </div>
                  </div>
                </SectionCard>

                {/* 2 — Pricing */}
                <SectionCard
                  step={2}
                  title="Pricing"
                  subtitle="Annual subscription price. Stripe Price ID is the recurring annual price."
                >
                  {/* Paid / Free toggle */}
                  <div className="flex border border-gray-200 rounded-[7px] overflow-hidden w-fit mb-4">
                    {['Paid', 'Free'].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => { setFieldValue('pricingMode', opt); setPricingMode(opt); }}
                        className="px-5 py-[9px] text-[13px] font-medium cursor-pointer select-none transition-all"
                        style={{
                          background: values.pricingMode === opt ? '#1d4ed8' : '#fff',
                          color: values.pricingMode === opt ? '#fff' : '#374151',
                          border: values.pricingMode === opt ? '1.5px solid #1d4ed8' : '1px solid transparent',
                          borderRadius: values.pricingMode === opt ? '6px' : 0,
                        }}
                      >
                        {opt === 'Paid' ? 'Paid Plan' : 'Free Plan'}
                      </button>
                    ))}
                  </div>

                  {values.pricingMode === 'Free' ? (
                    <div className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3">
                      <p className="text-[13px] font-semibold text-green-800">Free access — no payment required</p>
                      <p className="text-[12px] text-green-700 mt-[2px]">
                        Prices will be set to ₹0 / $0. No Stripe product will be linked.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-4 mb-4">
                        <div className="flex-1">
                          <FieldLabel required>Price (₹/year)</FieldLabel>
                          <TextField fullWidth name="price" type="number" placeholder="4999" value={values.price} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.price && errors.price)} helperText={touched.price && errors.price} InputProps={{ startAdornment: <span className="text-gray-500 mr-1 text-[13px]">₹</span> }} size="small" />
                        </div>
                        <div className="flex-1">
                          <FieldLabel required>Price (USD/year)</FieldLabel>
                          <TextField fullWidth name="priceUSD" type="number" placeholder="59.99" value={values.priceUSD} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched.priceUSD && errors.priceUSD)} helperText={touched.priceUSD && errors.priceUSD} InputProps={{ startAdornment: <span className="text-gray-500 mr-1 text-[13px]">$</span> }} size="small" />
                        </div>
                        <div className="flex-1">
                          <FieldLabel>Billing cycle</FieldLabel>
                          <BillingToggle value={values.billingCycle} onChange={(v) => setFieldValue('billingCycle', v)} />
                        </div>
                      </div>
                      <div>
                        <FieldLabel>Stripe Price ID</FieldLabel>
                        <TextField fullWidth name="stripePriceId" InputProps={{ readOnly: true }} placeholder="price_annual_ ..." value={values.stripePriceId} onChange={handleChange} onBlur={handleBlur} size="small" />
                        <p className="text-[11px] text-gray-400 mt-1">Recurring annual Stripe Price ID for checkout.</p>
                      </div>
                    </>
                  )}
                </SectionCard>

                {/* 3 — Feature Bullets & Footnote */}
                <SectionCard
                  step={3}
                  title="Feature Bullets & Footnote"
                  subtitle="Marketing copy for the pricing card. Max 7 bullets, 80 chars each."
                >
                  <FieldLabel>Feature bullets</FieldLabel>
                  <FieldArray name="featureBullets">
                    {({ push, remove }) => (
                      <FeatureBullets
                        values={values}
                        handleChange={handleChange}
                        push={push}
                        remove={remove}
                      />
                    )}
                  </FieldArray>
                  <div className="mt-2">
                    <FieldLabel>Footnote</FieldLabel>
                    <TextField
                      fullWidth
                      name="footnote"
                      placeholder="e.g. Annual subscription. Renews automatically."
                      value={values.footnote}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                    />
                  </div>
                </SectionCard>

                {/* 4 — Content (E-Books / Courses / Assessments) */}
                <SectionCard
                  step={4}
                  title="Content"
                  subtitle="Select content to include in this plan. At least one e-book, course, or assessment is required."
                >
                  {/* Tab bar */}
                  <ContentTabBar active={contentTab} onChange={setContentTab} />

                  {/* Selected counts summary */}
                  <div className="flex gap-3 mb-3">
                    {CONTENT_TABS.map((t) => {
                      const fieldMap = {
                        ebooks: 'selectedEbooks',
                        courses: 'selectedCourses',
                        assessments: 'selectedAssessments',
                      };
                      const count = values[fieldMap[t.key]].length;
                      return count > 0 ? (
                        <span
                          key={t.key}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {count} {t.label}
                        </span>
                      ) : null;
                    })}
                  </div>

                  {/* Search */}
                  <div className="mb-2">
                    <SearchBox
                      value={activeConfig.search}
                      onChange={(e) => activeConfig.setSearch(e.target.value)}
                      placeholder={activeConfig.placeholder}
                    />
                  </div>

                  {/* List */}
                  <div
                    className={`border rounded-[7px] max-h-[240px] overflow-y-auto ${
                      submitCount > 0 && errors.selectedEbooks
                        ? 'border-red-400'
                        : 'border-gray-200'
                    }`}
                  >
                    <ItemList
                      loading={contentLoading}
                      items={activeConfig.items}
                      selectedIds={values[activeConfig.field]}
                      onToggle={(id, checked) => toggleId(activeConfig.field, id, checked)}
                      emptyText={activeConfig.emptyText}
                    />
                  </div>

                  {/* Validation error for e-books */}
                  {submitCount > 0 && errors.selectedEbooks && (
                    <p className="text-[11px] text-red-500 mt-1">{errors.selectedEbooks}</p>
                  )}
                </SectionCard>

                {/* 5 — Cover Image */}
                <SectionCard
                  step={5}
                  title="Cover Image"
                  subtitle="Min 1200 × 400 px · JPG, PNG or BMP · Max 100 MB"
                >
                  <CoverImageUpload
                    existingUrl={null}
                    coverImage={coverImage}
                    preview={coverImgPreview}
                    onFileChange={handleFileChange}
                    onRemove={() => {
                      setCoverImage(null);
                      setCoverImgPreview(null);
                      setCoverImageError('');
                    }}
                    error={coverImageError}
                  />
                </SectionCard>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 pb-10">
                  <button
                    type="button"
                    onClick={() => navigate(`/app/subscription?tab=${fromTab}`)}
                    className="text-[13px] font-semibold text-[#374151] cursor-pointer px-5 py-[6px] rounded-[8px] border border-[#d1d5db] bg-white hover:border-[#9ca3af] hover:bg-[#f9fafb] transition-all duration-150"
                  >
                    Cancel
                  </button>
                  <div className="flex gap-[10px]">
                    <LoadingButton
                      type="submit"
                      loading={submittingDraft}
                      variant="outlined"
                      onClick={() => { publishModeRef.current = 'draft'; }}
                      sx={{
                        fontSize: '13px', fontWeight: 600, borderRadius: '8px',
                        borderColor: '#d1d5db', color: '#374151', textTransform: 'none', px: '20px',
                      }}
                    >
                      Save as Draft
                    </LoadingButton>
                    <LoadingButton
                      type="submit"
                      loading={submittingPublish}
                      variant="contained"
                      onClick={() => { publishModeRef.current = 'publish'; }}
                      sx={{
                        fontSize: '13px', fontWeight: 600, borderRadius: '8px',
                        background: '#2563eb', textTransform: 'none', px: '20px',
                        '&:hover': { background: '#1d4ed8' },
                      }}
                    >
                      Publish Plan
                    </LoadingButton>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      <SnackBar
        open={snackbar.open}
        snackbarTitle={snackbar.title}
        close={() => setSnackbar({ open: false, title: '' })}
      />
    </Page>
  );
};

export default CreateEbookPlan;